import { AxiosResponse } from "axios";
import { FC, PropsWithChildren, ReactNode, useEffect, useRef, useState } from "react";
import { isCancelledErrorProcessor } from "../../../utils/AppUtils";

export interface AbstractInfiniteScrollProps<T = any, U = any> {
    getData: (
        signal: AbortSignal,
        pageNumber: number,
    ) => Promise<
        AxiosResponse<{
            data: any[];
            lastPage: boolean;
            pageNumber: number;
        }>
    >;
    loader?: ReactNode;
    noResults?: ReactNode;
    reset?: boolean;
    onData: (data: AxiosResponse<T, U> | null) => void;
}

const DEBUG_MODE = false;
// const DEBUG_MODE = true;

const AbstractInfiniteScroll: FC<PropsWithChildren<AbstractInfiniteScrollProps>> = ({ children, noResults, loader, getData, onData, reset }) => {
    const targetItem = useRef<HTMLDivElement>(null);
    const pageNumber = useRef<number>(0);
    const doneRef = useRef<boolean>(false);
    const initialized = useRef<boolean>(false);
    const observer = useRef<IntersectionObserver>(undefined);

    const [done, setDone] = useState<boolean>(false);
    const [missingResult, setMissingResult] = useState<boolean>(false);

    const [load, setLoad] = useState<boolean>(false);
    const [showTarget, setShowTarget] = useState<boolean>(true);

    //  Synchronize doneRef and done (state) only when ref changes
    //  The doneRef is updated directly inside the observer, but since useRef does not trigger re-renders,
    //  the UI will not reflect this update unless we manually synchronize the done state with doneRef.current.
    //  This useEffect is needed to ensure that when doneRef.current changes, the state (done) is also updated.

    useEffect(() => {
        if (doneRef.current !== done) {
            setDone(doneRef.current);
        }
    });

    useEffect(() => {
        if (initialized.current) {
            initialized.current = false;
            setMissingResult(false);
            pageNumber.current = 0;
            doneRef.current = false;
            setLoad(true);
        }
    }, [onData, getData, reset]);

    useEffect(() => {
        if (initialized.current || !targetItem.current) {
            return undefined;
        }

        initialized.current = true;
        let loading = false;
        const controller = new AbortController();

        observer.current = new IntersectionObserver((entries, observerRef) => {
            if (doneRef.current) {
                controller.abort();
                observerRef.takeRecords();
                return;
            }

            if (entries[entries.length - 1].isIntersecting && !loading) {
                loading = true;
                setShowTarget(false);
                getData(controller.signal, pageNumber.current)
                    .then((res) => {
                        const isMissingResult = !res.data?.data?.length;
                        setMissingResult(isMissingResult);
                        doneRef.current = res.data.lastPage;
                        pageNumber.current = res.data.pageNumber + 1;

                        return onData(res);
                    })
                    .catch(isCancelledErrorProcessor)
                    .catch((e) => {
                        // stop calling erroring api-s
                        setMissingResult(true);
                        doneRef.current = true;

                        throw e;
                    })
                    .finally(() => {
                        loading = false;
                        setLoad(false);
                        setShowTarget(true);
                    });
            }
        });

        observer.current.observe(targetItem.current);

        return () => {
            observer.current?.takeRecords();
            observer.current?.disconnect();
            controller.abort();
        };
    }, [onData, getData, reset]);

    return (
        <>
            {children}
            {done && missingResult ? noResults : null}
            {(!done || load) && <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>{loader}</div>}
            <div
                style={{
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    height: !done ? "20px" : "unset",
                    alignItems: "center",
                    ...(DEBUG_MODE ? { border: "2px solid red" } : {}),
                }}
            >
                <div
                    ref={targetItem}
                    style={{
                        position: "absolute",
                        height: "60px",
                        width: "100%",
                        top: "-60px",
                        display: showTarget ? "block" : "none",
                        ...(DEBUG_MODE ? { border: "2px solid green" } : { visibility: "hidden" }),
                    }}
                />
            </div>
        </>
    );
};

export default AbstractInfiniteScroll;
